import { useEffect, useState } from "react";
import axios from "axios";
import { Car, Leaf, Store, Smartphone, Banknote, ChevronRight, PhoneCall, Mail, MapPin, Globe } from "lucide-react";
import { useSelectedCountry } from "@/context/SelectedCountryContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo sources, tried in order. Google's faviconV2 returns a high-res PNG
// (up to 128px); DuckDuckGo's ip3 is a reliable secondary. If both fail we
// fall back to a coloured-initials badge.
const logoSources = (domain) => [
  `https://www.google.com/s2/favicons?sz=128&domain=${domain}`,
  `https://icons.duckduckgo.com/ip3/${domain}.ico`,
];

function BrandLogo({ name, domain, size = 56 }) {
  const sources = domain ? logoSources(domain) : [];
  const [idx, setIdx] = useState(0);
  const exhausted = !domain || idx >= sources.length;

  if (exhausted) {
    const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
    return (
      <div
        data-testid={`brand-fallback-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="shrink-0 rounded-md bg-[#EEF2FF] text-[#002FA7] font-bold flex items-center justify-center"
        style={{ width: size, height: size, fontSize: size * 0.36 }}
        aria-label={name}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={sources[idx]}
      alt={`${name} logo`}
      onError={() => setIdx((i) => i + 1)}
      loading="lazy"
      data-testid={`brand-logo-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="shrink-0 rounded-md border border-[#E2E8F0] bg-white object-contain"
      style={{ width: size, height: size, padding: 4 }}
    />
  );
}

function ItemCard({ name, domain, note }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-md border border-[#E2E8F0] bg-white hover:border-[#002FA7]/40 transition-colors">
      <BrandLogo name={name} domain={domain} />
      <div className="min-w-0">
        <h4 className="font-display text-base font-bold tracking-tight text-[#0F172A] leading-tight">{name}</h4>
        {note && <p className="mt-1 text-sm text-[#475569] leading-relaxed">{note}</p>}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <section className="mt-12" data-testid={`locals-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-md bg-[#002FA7] text-white flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0F172A]">{title}</h2>
          {count !== undefined && <div className="text-xs text-[#475569] tracking-wider uppercase">{count} options</div>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function Locals() {
  const { slug, country } = useSelectedCountry();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setData(null);
    axios.get(`${API}/locals/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="fade-in max-w-6xl mx-auto px-6 lg:px-12 py-16" data-testid="locals-page">
      <div className="mb-10 max-w-2xl">
        <div className="overline mb-3">On-the-ground essentials</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]">
          Food, Taxi &amp; Locals
          {country && (
            <span className="block text-2xl sm:text-3xl font-bold text-[#002FA7] mt-2">
              <span className="mr-2">{country.flag}</span>{country.name}
            </span>
          )}
        </h1>
        <p className="mt-4 text-[#475569] text-lg leading-relaxed">
          The practical answers: which ride app works here, where to find vegetarian/vegan groceries,
          which convenience store chain runs the city, your roaming-vs-local-SIM call, and where
          locals exchange money.
        </p>
      </div>

      {loading && <div className="text-[#475569] py-12">Loading…</div>}

      {data && !loading && (
        <>
          <Section icon={Car} title="Ride-Share Apps" count={data.ride_share?.length}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(data.ride_share || []).map((it, i) => <ItemCard key={i} {...it} />)}
            </div>
          </Section>

          <Section icon={Leaf} title="Veg & Vegan in Supermarkets" count={data.veg_food?.length}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(data.veg_food || []).map((it, i) => <ItemCard key={i} {...it} />)}
            </div>
          </Section>

          <Section icon={Store} title="Convenience Stores" count={data.convenience?.length}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(data.convenience || []).map((it, i) => <ItemCard key={i} {...it} />)}
            </div>
          </Section>

          <Section icon={Smartphone} title="SIM &amp; Roaming">
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="p-5 rounded-md border border-[#E2E8F0] bg-white">
                <div className="overline mb-3">Indian carriers (international roaming)</div>
                <ul className="space-y-2 text-sm" data-testid="locals-roaming">
                  {(data.sim?.indian_roaming || []).map((r, i) => (
                    <li key={i} className="flex items-start justify-between gap-4">
                      <span className="font-semibold text-[#0F172A]">{r.carrier}</span>
                      <span className="text-[#475569] text-right">{r.plan}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-5 rounded-md border border-[#002FA7]/30 bg-[#EEF2FF]" data-testid="locals-local-sim">
                <div className="overline mb-3 text-[#002FA7]">Local SIM recommendation</div>
                <p className="text-sm text-[#0F172A] leading-relaxed">{data.sim?.local_sim}</p>
              </div>
            </div>
          </Section>

          <Section icon={Banknote} title="Currency Exchange" count={data.currency?.length}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(data.currency || []).map((it, i) => <ItemCard key={i} {...it} />)}
            </div>
          </Section>

          {data.emergency && (
            <Section icon={PhoneCall} title="Emergency & Embassy">
              <div className="grid lg:grid-cols-2 gap-4">
                {/* India MEA universal helpline */}
                <div className="p-5 rounded-md border border-[#FCA5A5] bg-[#FEF2F2]" data-testid="locals-india-helpline">
                  <div className="overline mb-3 text-[#B91C1C]">India — MEA helpline (24×7)</div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start justify-between gap-4">
                      <span className="font-semibold text-[#0F172A]">MEA Control Room (Delhi)</span>
                      <a href="tel:+911123012113" className="text-[#B91C1C] font-medium text-right">+91-11-2301-2113</a>
                    </li>
                    <li className="flex items-start justify-between gap-4">
                      <span className="font-semibold text-[#0F172A]">Toll-free (within India)</span>
                      <a href="tel:18001112014" className="text-[#B91C1C] font-medium text-right">1800-11-2014</a>
                    </li>
                    <li className="flex items-start justify-between gap-4">
                      <span className="font-semibold text-[#0F172A]">Madad portal</span>
                      <a href="https://madad.gov.in" target="_blank" rel="noreferrer" className="text-[#B91C1C] font-medium text-right">madad.gov.in</a>
                    </li>
                  </ul>
                  <p className="mt-3 text-xs text-[#7F1D1D] leading-relaxed">Lost passport, distress, family emergency — your fastest route to MEA help while abroad.</p>
                </div>

                {/* Local emergency numbers */}
                <div className="p-5 rounded-md border border-[#002FA7]/30 bg-[#EEF2FF]" data-testid="locals-local-emergency">
                  <div className="overline mb-3 text-[#002FA7]">{data.country} — local emergency numbers</div>
                  <ul className="space-y-2 text-sm">
                    {(data.emergency.local || []).map((it, i) => (
                      <li key={i} className="flex items-start justify-between gap-4">
                        <span className="font-semibold text-[#0F172A]">{it.label}</span>
                        <a href={`tel:${it.number.replace(/\s+/g, "")}`} className="text-[#002FA7] font-bold text-right">{it.number}</a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Indian Embassy card — spans both columns on large screens */}
                <div className="lg:col-span-2 p-5 rounded-md border border-[#E2E8F0] bg-white" data-testid="locals-embassy">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-md bg-[#0F172A] text-white flex items-center justify-center text-lg">🇮🇳</div>
                    <div>
                      <div className="overline">Indian mission</div>
                      <h3 className="font-display text-lg font-bold tracking-tight text-[#0F172A]">{data.emergency.embassy.name}</h3>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 text-[#475569] shrink-0" strokeWidth={1.5} />
                      <span className="text-[#475569]">{data.emergency.embassy.address}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <PhoneCall className="w-4 h-4 mt-0.5 text-[#475569] shrink-0" strokeWidth={1.5} />
                      <div>
                        <a href={`tel:${data.emergency.embassy.phone.replace(/\s+/g, "")}`} className="text-[#0F172A] font-semibold block">{data.emergency.embassy.phone}</a>
                        <a href={`tel:${data.emergency.embassy.emergency_phone.replace(/\s+/g, "")}`} className="text-[#B91C1C] text-xs">After-hours / emergency: {data.emergency.embassy.emergency_phone}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 mt-0.5 text-[#475569] shrink-0" strokeWidth={1.5} />
                      <a href={`mailto:${data.emergency.embassy.email}`} className="text-[#002FA7] font-medium break-all">{data.emergency.embassy.email}</a>
                    </div>
                    <div className="flex items-start gap-2">
                      <Globe className="w-4 h-4 mt-0.5 text-[#475569] shrink-0" strokeWidth={1.5} />
                      <a href={data.emergency.embassy.website} target="_blank" rel="noreferrer" className="text-[#002FA7] font-medium break-all">{data.emergency.embassy.website.replace(/^https?:\/\//, "")}</a>
                    </div>
                  </div>
                </div>
              </div>
            </Section>
          )}

          <p className="mt-12 text-xs text-[#94A3B8] italic">
            Prices and brand availability change frequently — please confirm before travel.
          </p>
        </>
      )}
    </div>
  );
}

// Compact home-page tile (default export named via named import below)
export function LocalsHomeTile() {
  return (
    <a
      href="/locals"
      data-testid="home-locals-tile"
      className="group block card overflow-hidden hover:-translate-y-1 transition-all duration-300"
    >
      <div className="p-7 lg:p-9 flex items-start gap-6">
        <div className="shrink-0 grid grid-cols-2 gap-1.5">
          <div className="w-10 h-10 rounded-md bg-[#002FA7] text-white flex items-center justify-center"><Car className="w-5 h-5" strokeWidth={1.5} /></div>
          <div className="w-10 h-10 rounded-md bg-[#0F766E] text-white flex items-center justify-center"><Leaf className="w-5 h-5" strokeWidth={1.5} /></div>
          <div className="w-10 h-10 rounded-md bg-[#B45309] text-white flex items-center justify-center"><Store className="w-5 h-5" strokeWidth={1.5} /></div>
          <div className="w-10 h-10 rounded-md bg-[#7C3AED] text-white flex items-center justify-center"><Banknote className="w-5 h-5" strokeWidth={1.5} /></div>
        </div>
        <div className="flex-1">
          <div className="overline mb-2">On-the-ground essentials</div>
          <h3 className="font-display text-2xl font-black tracking-tight text-[#0F172A]">
            Food, Taxi &amp; Locals
          </h3>
          <p className="mt-2 text-[#475569] text-sm leading-relaxed max-w-lg">
            Ride apps that work, supermarket veg/vegan picks, convenience chains, roaming-vs-local-SIM,
            and trusted currency-exchange spots — for all 12 destinations.
          </p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#002FA7]">
            Browse by country
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
          </div>
        </div>
      </div>
    </a>
  );
}
